// FILE: packages/components/src/admin/tasks/ProjectKanbanBoard.tsx

import React, { useState, useMemo } from 'react';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, closestCenter } from '@dnd-kit/core';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import clsx from 'clsx';
import { toast } from '@dopomogai/ui';
import { v4 as uuidv4 } from 'uuid';

import { Button, Spinner } from '@dopomogai/ui';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui'; // Adjust path
import { useManageTask, useManageTaskAssignment } from '@dopomogai/agent-kit';
import type { ProjectTask, Sprint, TaskPayload } from '@dopomogai/types';

import { DraggableTaskCard } from './DraggableTaskCard';
import { TaskDetailView } from './TaskDetailView';
import { KanbanColumn } from './KanbanColumn';

// Define the structure for our Kanban column data
interface KanbanColumnData {
  id: string; // e.g., 'backlog', or a sprint ID
  title: string;
  tasks: ProjectTask[];
}

interface ProjectKanbanBoardProps {
  tasks: ProjectTask[];
  sprints: Sprint[];
  projectId: string;
  isLoading?: boolean;
  onDragEnd: (event: DragEndEvent) => void;
  // Callback to signal that the parent should re-fetch all tasks
  onTasksChange: () => void; 
}

/**
 * A full-featured Kanban board with inline task creation and editing.
 */
export const ProjectKanbanBoard: React.FC<ProjectKanbanBoardProps> = ({
  tasks,
  sprints,
  projectId,
  isLoading,
  onDragEnd,
  onTasksChange,
}) => {
  const { t } = useTranslation('admin_projects');
  
  // --- LOCAL UI STATE ---
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [activeDragTask, setActiveDragTask] = useState<ProjectTask | null>(null);
  const [inlineCreationTask, setInlineCreationTask] = useState<ProjectTask | null>(null);

  // --- MUTATION HOOKS ---
  const { createTask, isLoading: isCreating } = useManageTask();
  const { updateTask, isLoading: isUpdating } = useManageTask(); // Separate for clarity
  
  // --- DERIVED STATE: MERGE REAL & TEMP TASKS ---
  const allDisplayTasks = useMemo(() => {
    return inlineCreationTask ? [inlineCreationTask, ...(tasks || [])] : (tasks || []);
  }, [tasks, inlineCreationTask]);

  const columns = useMemo((): KanbanColumnData[] => {
    const backlogTasks = allDisplayTasks.filter(t => !t.sprint_id);
    const sprintColumns: KanbanColumnData[] = sprints.map(sprint => ({
      id: sprint.id,
      title: sprint.name,
      tasks: allDisplayTasks.filter(t => t.sprint_id === sprint.id),
    }));
    return [
      { id: 'backlog', title: t('kanban.backlogTitle'), tasks: backlogTasks },
      ...sprintColumns,
    ];
  }, [allDisplayTasks, sprints, t]);
  
  // --- DRAG & DROP HANDLERS ---
  const handleDragStart = (event: DragStartEvent) => {
    const task = event.active.data.current?.task as ProjectTask;
    setActiveDragTask(task);
    setExpandedTaskId(null);
    setInlineCreationTask(null); // Cancel creation on drag start
  };
  
  const handleInternalDragEnd = (event: DragEndEvent) => {
    setActiveDragTask(null);
    onDragEnd(event);
  };

  // --- INLINE CREATION HANDLERS ---
  const handleStartInlineCreate = () => {
    if (inlineCreationTask) return;
    setExpandedTaskId(null);

    const tempTask: ProjectTask = {
      _id: `new-task-${uuidv4()}`, // Client-side temporary ID
      project_id: projectId,
      sprint_id: null, // New tasks always start in the backlog
      title: '',
      status: 'todo',
      task_type: 'feature',
      points: 1,
      version: 1,
      links: [],
      checklist: [],
      assignee_ids: [],
      created_at: '', // Mark as new by having no creation date
      updated_at: '',
    };
    setInlineCreationTask(tempTask);
  };

  const handleCancelInlineCreate = () => {
    setInlineCreationTask(null);
  };

  const handleConfirmInlineCreate = async (payload: TaskPayload) => {
    const result = await createTask(projectId, payload);
    if (result) {
      toast.success(`Task "${result.title}" created.`);
      setInlineCreationTask(null);
      onTasksChange(); // Signal parent to re-fetch
    }
  };
  
  // --- INLINE UPDATE HANDLER ---
  const handleConfirmUpdate = async (payload: TaskPayload) => {
    if (!payload._id) {
      toast.error('Cannot update task: missing task ID.');
      return;
    }

    const result = await updateTask(payload._id, payload);
    if (result) {
        toast.success(`Task "${result._id}" updated.`);
        setExpandedTaskId(null); // Close the editor on successful save
        onTasksChange(); // Signal parent to re-fetch
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 flex items-center justify-center h-full w-full bg-slate-100">
        <Spinner text={t('kanban.loading')} size="lg"/>
      </div>
    );
  }
    
  return (
    <DndContext 
        onDragStart={handleDragStart} 
        onDragEnd={handleInternalDragEnd}
        collisionDetection={closestCenter}
    >
      <div className="flex h-full w-full p-4 gap-4 overflow-x-auto bg-slate-100 rounded-lg">
        {columns.map(column => (
          <KanbanColumn key={column.id} id={column.id} className="w-80 flex-shrink-0 bg-slate-200/70 rounded-xl flex flex-col">
            {/* ... Column Header ... */}
            <div className="p-3 space-y-3 overflow-y-auto flex-grow">
              <AnimatePresence>
                {column.tasks.map(task => {
                  const isBeingDragged = activeDragTask?._id === task._id;

                  // --- CONDITIONAL RENDER LOGIC ---
                  if (inlineCreationTask && task._id === inlineCreationTask._id) {
                    return (
                      <TaskDetailView
                        key={task._id}
                        task={task}
                        onClose={handleCancelInlineCreate}
                        onSave={handleConfirmInlineCreate}
                        isSaving={isCreating}
                        saveButtonTextKey="common:createTask"
                      />
                    );
                  } else if (expandedTaskId === task._id) {
                    return (
                      <TaskDetailView
                        key={task._id}
                        task={task}
                        onClose={() => setExpandedTaskId(null)}
                        onSave={handleConfirmUpdate}
                        isSaving={isUpdating}
                        saveButtonTextKey="common:saveChanges"
                      />
                    );
                  } else {
                    return (
                      <motion.div
                        layoutId={`task-card-${task._id}`}
                        key={task._id}
                        className={clsx(isBeingDragged && 'opacity-0')}
                      >
                        <DraggableTaskCard
                          task={task}
                          onExpand={() => {
                            setExpandedTaskId(task._id);
                            setInlineCreationTask(null); // Cancel creation if expanding another card
                          }}
                        />
                      </motion.div>
                    );
                  }
                })}
              </AnimatePresence>
            </div>
            {/* Add Task Button for Backlog Column */}
            {column.id === 'backlog' && (
                <div className="p-3 mt-auto border-t-2 border-slate-300/80">
                    <Button 
                        textKey="kanban.newTaskButton" 
                        variant="ghost"
                        icon={<Plus size={16}/>} 
                        fullWidth
                        onClick={handleStartInlineCreate}
                        disabled={isCreating} // Disable while an inline creation is active
                    />
                </div>
            )}
          </KanbanColumn>
        ))}
      </div>
      
      <DragOverlay>
      {activeDragTask ? <DraggableTaskCard task={activeDragTask} isDragging onExpand={() => {}} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
